import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import RootLayout from './layouts/RootLayout';
import AwardSurveyPage from './pages/Award/Edit/AwardSurveyPage';
import CommitteeDatePage from './pages/Committee/Edit/CommitteeDatePage';

const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const PublicLayout = lazy(() => import('./layouts/PublicLayout'));
const AwardDatePage = lazy(() => import('./pages/Award/Edit/AwardDatePage'));

export const getRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      element: <RootLayout />,
      children: [
        {
          path: '/',
          lazy: () => import('./pages/LandingPage'),
        },
        {
          path: '/error',
          element: <ErrorPage />,
        },
        //Auth routes
        {
          element: <PublicLayout contentClass="auth-bg" />,
          errorElement: <ErrorPage />,
          children: [
            {
              path: '/login',
              lazy: () => import('./pages/Auth/LoginPage'),
            },
            {
              path: '/logout/:method?',
              async lazy() {
                const { loader, LogoutPage } = await import('./pages/Auth/LogoutPage');
                return {
                  loader: loader,
                  Component: LogoutPage,
                };
              },
            },
            {
              path: '/recovery-code',
              lazy: () => import('./pages/Auth/RecoveryCodePage'),
            },
            {
              path: '/request-password-reset',
              lazy: () => import('./pages/Auth/RequestPasswordResetPage'),
            },
            {
              path: '/reset-password/id/:id/token/:token',
              lazy: () => import('./pages/Auth/ResetPasswordPage'),
            },
            {
              path: '/validate-otp',
              lazy: () => import('./pages/Auth/ValidateOTPPage'),
            },
            {
              path: '/verify-email/id/:id/token/:token',
              async lazy() {
                const { loader, VerifyEmailPage } = await import('./pages/Auth/VerifyEmailPage');
                return {
                  loader: loader(queryClient),
                  Component: VerifyEmailPage,
                };
              },
            },
            {
              path: '/verify-otp',
              lazy: () => import('./pages/Auth/VerifyOTPPage'),
            },
          ],
        },

        //Logged in admin routes
        {
          async lazy() {
            const { ProtectedRoute } = await import('./pages/ProtectedRoute');
            return {
              Component: ProtectedRoute,
            };
          },
          children: [
            {
              errorElement: <ErrorPage />,
              lazy: () => import('./layouts/MainLayout'),
              children: [
                {
                  errorElement: <ErrorPage />,
                  children: [
                    {
                      path: '/dashboard',
                      lazy: () => import('./pages/DashboardPage'),
                    },
                    {
                      path: '/async-jobs',
                      lazy: () => import('./pages/Job/JobListPage'),
                    },
                    {
                      path: '/alumni-surveys/:id/view',
                      lazy: () => import('./pages/Survey/AlumniSurveyResponseViewPage'),
                    },
                    {
                      path: '/allocations/add',
                      handle: { permissions: ['P43'] },
                      lazy: () => import('./pages/Allocation/AllocationAddPage'),
                    },
                    {
                      path: '/allocations/:id/edit',
                      handle: { permissions: ['P43'] },
                      lazy: () => import('./pages/Allocation/AllocationEditPage'),
                    },
                    {
                      path: '/allocations/:id/awards/:award_id/conditional-offers',
                      handle: { permissions: ['P57'] },
                      lazy: () => import('./pages/Allocation/AllocationConditionalOfferPage'),
                    },
                    {
                      path: '/beneficiaries/:id/offer-letter-preview',
                      handle: { permissions: ['P57'] },
                      lazy: () => import('./pages/Allocation/AllocationOfferLetterPreviewPage'),
                    },
                    {
                      path: '/allocations/list',
                      handle: { permissions: ['P43'] },
                      lazy: () => import('./pages/Allocation/AllocationListPage'),
                    },
                    {
                      path: '/allocations/overview-list',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/Allocation/AllocationOverviewListPage'),
                    },
                    {
                      path: '/allocations/:id/overview',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/Allocation/AllocationOverviewPage'),
                    },
                    {
                      path: '/allocations/:id/interviews',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/Allocation/AllocationInterviewListPage'),
                    },
                    {
                      path: '/allocations/:id/selection',
                      handle: { permissions: ['P57'] },
                      lazy: () => import('./pages/Allocation/AllocationSelectionPage'),
                    },
                    {
                      path: '/allocations/:id/selection-approval',
                      handle: { approval: true, permissions: ['P44'] },
                      lazy: () => import('./pages/Allocation/AllocationSelectionPage'),
                    },
                    {
                      path: '/allocations/:id/shortlisting',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/Allocation/AllocationShortlistingPage'),
                    },
                    {
                      path: '/allocations/:id/interview-schedules/add',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/InterviewSchedule/InterviewScheduleAddPage'),
                    },
                    {
                      path: '/allocations/:id/interview-schedules/list',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/InterviewSchedule/InterviewScheduleListPage'),
                    },
                    {
                      path: '/allocations/:allocation_id/interview-schedules/:id/edit',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/InterviewSchedule/InterviewScheduleEditPage'),
                    },
                    {
                      path: '/allocations/:allocation_id/interview-schedules/:id/view',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/InterviewSchedule/InterviewScheduleViewPage'),
                    },
                    {
                      path: '/allocations/:id/interview-schedules/invite',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/InterviewSchedule/InterviewScheduleInvitePage'),
                    },
                    {
                      path: '/allocations/:allocation_id/interview-schedules/:interview_schedule_id/interview-slots/add',
                      handle: { permissions: ['P44'] },
                      lazy: () => import('./pages/InterviewSlot/InterviewSlotAddPage'),
                    },
                    {
                      path: '/awards/add',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/AwardAddPage'),
                    },
                    {
                      path: '/awards/list',
                      handle: { permissions: ['P45'] },
                      lazy: () => import('./pages/Award/AwardListPage'),
                    },
                    {
                      path: '/awards/:id/overview',
                      handle: { permissions: ['P45'] },
                      lazy: () => import('./pages/Award/AwardOverviewPage'),
                    },
                    {
                      path: '/awards/:id/view',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/AwardViewPage'),
                    },
                    {
                      path: '/awards/:id/about-you-survey',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardSurveyPage
                          fieldName="about_you_survey_id"
                          label="'About you' survey"
                          surveyType="about-you-survey"
                        />
                      ),
                    },
                    {
                      path: '/awards/:id/academic-year',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardAcademicYearPage'),
                    },
                    {
                      path: '/awards/:id/additional-information-survey',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardSurveyPage
                          fieldName="additional_information_survey_id"
                          label="Additional information survey"
                          surveyType="additional-information-survey"
                        />
                      ),
                    },
                    {
                      path: '/awards/:id/application-end',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardDatePage
                          fieldName="application_end_at"
                          label="Application deadline"
                          minDateFieldName="application_start_at"
                          minDateLabel="application start"
                        />
                      ),
                    },
                    {
                      path: '/awards/:id/application-start',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardDatePage
                          fieldName="application_start_at"
                          label="Application start"
                          maxDateFieldName="application_end_at"
                          maxDateLabel="application deadline"
                        />
                      ),
                    },
                    {
                      path: '/awards/:id/award-code',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardCodePage'),
                    },
                    {
                      path: '/awards/:id/award-name',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardNamePage'),
                    },
                    {
                      path: '/awards/:id/award-type',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardTypePage'),
                    },
                    {
                      path: '/awards/:id/budget',
                      handle: { permissions: ['P52'] },
                      lazy: () => import('./pages/Award/Edit/AwardBudgetPage'),
                    },
                    {
                      path: '/awards/:id/close-month',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardCloseMonthPage'),
                    },
                    {
                      path: '/awards/:id/description',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardDescriptionPage'),
                    },
                    {
                      path: '/awards/:id/eligibility-survey',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardSurveyPage
                          fieldName="eligibility_survey_id"
                          label="Eligibility survey"
                          surveyType="eligibility-survey"
                        />
                      ),
                    },
                    {
                      path: '/awards/:id/essay-survey',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardSurveyPage fieldName="essay_survey_id" label="Essay survey" surveyType="essay-survey" />
                      ),
                    },
                    {
                      path: '/awards/:id/hosting-institution',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardUKInstitutionPage'),
                    },
                    {
                      path: '/awards/:id/institution-end',
                      handle: { permissions: ['P66'] },
                      element: (
                        <AwardDatePage
                          fieldName="institution_end_at"
                          label="Institution deadline"
                          minDateFieldName="application_start_at"
                          minDateLabel="application start"
                        />
                      ),
                    },
                    {
                      path: '/awards/:id/letterhead',
                      handle: { permissions: ['P56'] },
                      lazy: () => import('./pages/Award/Edit/AwardLetterheadPage'),
                    },
                    {
                      path: '/awards/:id/placement-end',
                      handle: { permissions: ['P66'] },
                      element: <AwardDatePage fieldName="placement_end_at" label="Placement deadline" />,
                    },
                    {
                      path: '/awards/:id/referee-end',
                      handle: { permissions: ['P66'] },
                      element: <AwardDatePage fieldName="references_end_at" label="Refereee deadline" />,
                    },
                    {
                      path: '/awards/:id/rejections',
                      handle: { permissions: ['P54'] },
                      lazy: () => import('./pages/Award/AwardRejectionPage'),
                    },
                    {
                      path: '/awards/:id/signatures',
                      handle: { permissions: ['P56'] },
                      lazy: () => import('./pages/Award/AwardSignatureOverviewPage'),
                    },
                    {
                      path: '/awards/:id/signatures/:signatureTypeID',
                      handle: { permissions: ['P56'] },
                      lazy: () => import('./pages/Award/Edit/AwardSignaturePage'),
                    },
                    {
                      path: '/awards/:id/start-month',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardStartMonthPage'),
                    },
                    {
                      path: '/awards/:id/status',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardStatusPage'),
                    },
                    {
                      path: '/awards/:id/tenable-from',
                      handle: { permissions: ['P66'] },
                      element: <AwardDatePage fieldName="tenable_at" label="Award tenable from" />,
                    },
                    {
                      path: '/awards/:id/work-hours-required',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardWorkHoursRequiredPage'),
                    },
                    {
                      path: '/awards/:id/work-years-required',
                      handle: { permissions: ['P66'] },
                      lazy: () => import('./pages/Award/Edit/AwardWorkYearsRequiredPage'),
                    },
                    {
                      path: '/beneficiaries/list-chevening',
                      handle: { permissions: ['P8'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryListScholarsPageChevening'),
                    },
                    {
                      path: '/beneficiaries/list-chevening-applicants',
                      handle: { permissions: ['P8'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryListApplicantsPageChevening'),
                    },
                    {
                      path: '/beneficiaries/list-marshall',
                      handle: { permissions: ['P8'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryListPageMarshall'),
                    },
                    {
                      path: '/beneficiaries/list-marshall-applicants',
                      handle: { permissions: ['P8', 'P55'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryListApplicantsPageMarshall'),
                    },
                    {
                      path: '/beneficiaries/list-placement/chevening/:award_type_code',
                      handle: { permissions: ['P58'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryListPlacementPageChevening'),
                    },
                    {
                      path: '/beneficiaries/list-placement/marshall',
                      handle: { permissions: ['P58'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryListPlacementPageMarshall'),
                    },
                    {
                      path: '/beneficiaries/manage-alumni',
                      handle: { permissions: ['P8'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryManageAlumniPage'),
                    },
                    {
                      path: '/beneficiaries/:id/placement',
                      handle: { permissions: ['P62'] },
                      lazy: () => import('./pages/Placement/BeneficiaryPlacementPage'),
                    },
                    {
                      path: '/beneficiaries/:id/placement/course-details',
                      handle: { permissions: ['P62'] },
                      lazy: () => import('./pages/Placement/BeneficiaryPlacementCourseDetailsPage'),
                    },
                    {
                      path: '/beneficiaries/:id/placement/fees',
                      handle: { permissions: ['P62'] },
                      lazy: () => import('./pages/Placement/BeneficiaryPlacementFeesPage'),
                    },
                    {
                      path: '/beneficiaries/:id/placement/relevant-contacts',
                      handle: { permissions: ['P62'] },
                      lazy: () => import('./pages/Placement/BeneficiaryPlacementRelevantContactsPage'),
                    },
                    {
                      path: '/beneficiaries/:id/placement/generate',
                      handle: { permissions: ['P62'] },
                      lazy: () => import('./pages/Placement/BeneficiaryPlacementGeneratePage'),
                    },
                    {
                      path: '/beneficiaries/:id/placement/view/:study_year?',
                      handle: { permissions: ['P62'] },
                      lazy: () => import('./pages/Beneficiary/Admin/Profile/BeneficiaryPlacementViewPage'),
                    },
                    {
                      path: '/beneficiaries/report',
                      handle: { permissions: ['P8'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryReportPage'),
                    },
                    {
                      //permission handled in BeneficiaryEditPage (P10)
                      async lazy() {
                        const { BeneficiaryEditPage } = await import('./pages/Beneficiary/Admin/BeneficiaryEditPage');
                        return {
                          Component: BeneficiaryEditPage,
                        };
                      },
                      children: [
                        {
                          path: '/beneficiaries/:id/appear-in-events-lists',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryAppearInEventsListsPage'),
                        },
                        {
                          path: '/beneficiaries/:id/application-country',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryApplicationCountryPage'),
                        },
                        {
                          path: '/beneficiaries/:id/assessments/:assessment_id/deactivate',
                          lazy: () => import('./pages/Beneficiary/Admin/Assessment/AssessmentDeactivatePage'),
                        },
                        {
                          path: '/beneficiaries/:id/assessments/:assessment_id/reactivate',
                          lazy: () => import('./pages/Beneficiary/Admin/Assessment/AssessmentReactivatePage'),
                        },
                        {
                          path: '/beneficiaries/:id/automated-notifications',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryAutomatedNotificationsPage'),
                        },
                        {
                          path: '/beneficiaries/:id/award',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryAwardPage'),
                        },
                        {
                          path: '/beneficiaries/:id/beneficiary-status',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryStatusPage'),
                        },
                        {
                          path: '/beneficiaries/:id/budget/:study_year',
                          handle: { permissions: ['P51'] },
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryBudgetPage'),
                        },
                        {
                          path: '/beneficiaries/:id/cash-card-activation',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCashCardActivationPage'),
                        },
                        {
                          path: '/beneficiaries/:id/cash-card-status',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCashCardStatusPage'),
                        },
                        {
                          path: '/beneficiaries/:id/certified-education-history',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCertifiedEducationHistoryPage'),
                        },
                        {
                          path: '/beneficiaries/:id/client-care-letter/:study_year?',
                          lazy: () => import('./pages/BeneficiaryLetter/ClientCareLetterPage'),
                        },
                        {
                          path: '/beneficiaries/:id/college-name/:study_year',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCollegeNamePage'),
                        },
                        {
                          path: '/beneficiaries/:id/confirmation-of-award-letter/:study_year?',
                          lazy: () => import('./pages/BeneficiaryLetter/ConfirmationOfAwardLetterPage'),
                        },
                        {
                          path: '/beneficiaries/:id/committee-assessments/add',
                          lazy: () => import('./pages/Beneficiary/Admin/Assessment/BeneficiaryCommitteeAssessmentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/committee-assessments/:committee_assessment_id/delete',
                          lazy: () => import('./pages/Beneficiary/Admin/Assessment/CommitteeAssessmentDeletePage'),
                        },
                        {
                          path: '/beneficiaries/:id/committee-assessments/:committee_assessment_id/update',
                          lazy: () => import('./pages/Beneficiary/Admin/Assessment/BeneficiaryCommitteeAssessmentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/contact-person/:contactPersonTypeCode',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryContactPersonPage'),
                        },
                        {
                          path: '/beneficiaries/:id/course-structure',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCourseStructurePage'),
                        },
                        {
                          path: '/beneficiaries/:id/citizenship-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCitizenshipDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/citizenship-start-date',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryCitizenshipStartDatePage'),
                        },
                        {
                          path: '/beneficiaries/:id/date-of-birth',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryDateOfBirthPage'),
                        },
                        {
                          path: '/beneficiaries/:id/task-validation/:task_code/:study_year?',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryTaskValidationPage'),
                        },
                        {
                          path: '/beneficiaries/:id/dual-citizenship',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryDualCitizenshipPage'),
                        },
                        {
                          path: '/beneficiaries/:id/education-history-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEducationHistoryDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/eligibility-verification-duplicate',
                          handle: {
                            eligibilityType: 'duplicate',
                          },
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEligibilityVerificationPage'),
                        },
                        {
                          path: '/beneficiaries/:id/eligibility-verification/:eligibilityTypeID',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEligibilityVerificationPage'),
                        },
                        {
                          path: '/beneficiaries/:id/eligibility-verification-notes',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEligibilityVerificationNotesPage'),
                        },
                        {
                          path: '/beneficiaries/:id/email',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEmailPage'),
                        },
                        {
                          path: '/beneficiaries/:id/employment/:beneficiaryEmploymentID?',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEmploymentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/employment-status',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEmploymentStatusPage'),
                        },
                        {
                          path: '/beneficiaries/:id/ethnicity',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEthnicityPage'),
                        },
                        {
                          path: '/beneficiaries/:id/final-award-letter/:study_year?',
                          lazy: () => import('./pages/BeneficiaryLetter/FinalAwardLetterPage'),
                        },
                        {
                          path: '/beneficiaries/:id/flags',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryFlagPage'),
                        },
                        {
                          path: '/beneficiaries/:id/flight-details/:flightType',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryFlightDetailsPage'),
                        },
                        {
                          path: '/beneficiaries/:id/fcdo-post',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryFCDOPostPage'),
                        },
                        {
                          path: '/beneficiaries/:id/gender',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryGenderPage'),
                        },
                        {
                          path: '/beneficiaries/:id/health-information-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryHealthInformationDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/identification-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryIdentificationDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/interaction-history/:beneficiaryInteractionHistoryID?',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryInteractionHistoryPage'),
                        },
                        {
                          path: '/beneficiaries/:id/interests',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryInterestPage'),
                        },
                        {
                          path: '/beneficiaries/:id/interview-slot',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryInterviewSlotPage'),
                        },
                        {
                          path: '/beneficiaries/:id/name',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryNamePage'),
                        },
                        {
                          path: '/beneficiaries/:id/next-of-kin',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryNextOfKinPage'),
                        },
                        {
                          path: '/beneficiaries/:id/overseas-contact',
                          handle: {
                            contactType: 'overseas',
                          },
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryContactPage'),
                        },
                        {
                          path: '/beneficiaries/:id/other-citizenship',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryOtherCitizenshipCountryPage'),
                        },
                        {
                          path: '/beneficiaries/:id/passport',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryPassportPage'),
                        },
                        {
                          path: '/beneficiaries/:id/passport-country',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryPassportCountryPage'),
                        },
                        {
                          path: '/beneficiaries/:id/partners/add/:study_year',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryPartnerPage'),
                        },
                        {
                          path: '/beneficiaries/:id/pell-grant',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryPellGrantPage'),
                        },
                        {
                          path: '/beneficiaries/:id/personal-contact',
                          handle: {
                            contactType: 'personal',
                          },
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryContactPage'),
                        },
                        {
                          path: '/beneficiaries/:id/place-of-birth',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryPlaceOfBirthPage'),
                        },
                        {
                          path: '/beneficiaries/:id/preferred-name',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryPreferredNamePage'),
                        },
                        {
                          path: '/beneficiaries/:id/programme-officer',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryProgrammeOfficerPage'),
                        },
                        {
                          path: '/beneficiaries/:id/proposed-courses/:beneficiaryProposedCourseID',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryProposedCoursePage'),
                        },
                        {
                          path: '/beneficiaries/:id/proposed-courses/:beneficiaryProposedCourseID/proposed-course-status',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryProposedCourseStatusPage'),
                        },
                        {
                          path: '/beneficiaries/:id/reasonable-adjustments',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryReasonableAdjustmentsPage'),
                        },
                        {
                          path: '/beneficiaries/:id/reference-documents',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryReferenceDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/residence-permit',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryResidencePermitPage'),
                        },
                        {
                          path: '/beneficiaries/:id/scholarship-offer-status',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryScholarshipOfferStatusPage'),
                        },
                        {
                          path: '/beneficiaries/:id/signed-application-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiarySignedApplicationDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/social-media',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiarySocialMediaPage'),
                        },
                        {
                          path: '/beneficiaries/:id/study-year',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryStudyYearPage'),
                        },
                        {
                          path: '/beneficiaries/:id/supervisor-report-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiarySupervisorReportDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/transcript-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryTranscriptDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/travel-document',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryTravelDocumentPage'),
                        },
                        {
                          path: '/beneficiaries/:id/uk-contact/:study_year?',
                          handle: {
                            contactType: 'uk',
                          },
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryContactPage'),
                        },
                        {
                          path: '/beneficiaries/:id/uk-bank-account',
                          lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryUKBankAccountPage'),
                        },
                      ],
                    },
                    {
                      path: '/beneficiaries/:id/academic-assessment',
                      handle: { permissions: ['P20'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryAcademicAssessmentPage'),
                    },
                    {
                      path: '/beneficiaries/:id/applicant-view',
                      handle: { permissions: ['P9'] },
                      lazy: () => import('./pages/Beneficiary/Admin/ApplicantViewPage'),
                    },
                    {
                      path: '/beneficiaries/:id/application-view',
                      handle: { permissions: ['P32', 'P55'] },
                      lazy: () => import('./pages/Beneficiary/Admin/ApplicationViewPage'),
                    },
                    {
                      path: '/beneficiaries/:id/endorse',
                      handle: { permissions: ['P33', 'P75'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEndorsePage'),
                    },
                    {
                      path: '/beneficiaries/:id/endorsement-letter',
                      handle: { permissions: ['P33', 'P75'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryEndorsementPage'),
                    },
                    {
                      path: '/beneficiaries/:id/interview-outcome',
                      handle: { permissions: ['P10', 'P55'] },
                      lazy: () => import('./pages/Beneficiary/Admin/BeneficiaryInterviewOutcomePage'),
                    },
                    {
                      path: '/beneficiaries/:id/view',
                      handle: { permissions: ['P9'] },
                      lazy: () => import('./pages/Beneficiary/Admin/Profile/BeneficiaryProfileViewPage'),
                    },
                    {
                      path: '/beneficiaries/:beneficiaryID/beneficiary-recommenders/:beneficiaryRecommenderID/submit',
                      handle: { permissions: ['P33', 'P75'] },
                      lazy: () => import('./pages/Beneficiary/Admin/AdvisorRecommendationPage'),
                    },
                    {
                      path: '/budgets/:programme_code',
                      handle: { permissions: ['P52'] },
                      lazy: () => import('./pages/Budget/BudgetPage'),
                    },
                    {
                      path: '/committees/add',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/CommitteeAddPage'),
                    },
                    {
                      path: '/committees/list',
                      lazy: () => import('./pages/Committee/CommitteeListPage'),
                    },
                    {
                      path: '/committees/interviews',
                      handle: { permissions: ['P55'] },
                      lazy: () => import('./pages/Committee/CommitteeChairInterviewListPage'),
                    },
                    {
                      path: '/committees/:id/assessor-view',
                      handle: { permissions: ['P40'] },
                      lazy: () => import('./pages/Committee/CommitteeAssessorViewPage'),
                    },
                    {
                      path: '/committees/:id/assessors/add',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeAssessorAddPage'),
                    },
                    {
                      path: '/committees/:committee_id/assessors/:id/assessor-type',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeAssessorTypePage'),
                    },
                    {
                      path: '/committees/:committee_id/assessors/:id/end-scoring',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeAssessorDeactivatePage'),
                    },
                    {
                      path: '/committees/:committee_id/assessors/:id/reinstate',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeAssessorReactivatePage'),
                    },
                    {
                      path: '/committees/:committee_id/beneficiaries/:id/assessment',
                      handle: { permissions: ['P40'] },
                      lazy: () => import('./pages/Committee/BeneficiaryAssessmentPage'),
                    },
                    {
                      path: '/committees/:committee_id/beneficiaries/:id/committee-assessment',
                      handle: { permissions: ['P40'] },
                      lazy: () => import('./pages/Committee/BeneficiaryCommitteeAssessmentPage'),
                    },
                    {
                      path: '/committees/:committee_id/beneficiaries/:id/application-review',
                      handle: { permissions: ['P40', 'P55'] },
                      lazy: () => import('./pages/Committee/BeneficiaryApplicationReviewPage'),
                    },
                    {
                      path: '/committees/:id/committee-name',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeNamePage'),
                    },
                    {
                      path: '/committees/:id/committee-type',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeTypePage'),
                    },
                    {
                      path: '/committees/:id/is-locked',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeIsLockedPage'),
                    },
                    {
                      path: '/committees/:id/scoring-end',
                      handle: { permissions: ['P39'] },
                      element: <CommitteeDatePage fieldName="scoring_end_at" label="Scoring deadline" />,
                    },
                    {
                      path: '/committees/:id/scoring-rights',
                      handle: { permissions: ['P39'] },
                      lazy: () => import('./pages/Committee/Edit/CommitteeScoringRightsPage'),
                    },
                    {
                      path: '/committees/:id/view',
                      handle: { permissions: ['P39', 'P55'] },
                      lazy: () => import('./pages/Committee/CommitteeViewPage'),
                    },
                    {
                      path: '/countries/list',
                      handle: { permissions: ['P72'] },
                      lazy: () => import('./pages/Country/CountryListPage'),
                    },
                    {
                      path: '/countries/:id/edit',
                      handle: { permissions: ['P72'] },
                      lazy: () => import('./pages/Country/CountryEditPage'),
                    },
                    {
                      path: '/courses/list',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/CourseListPage'),
                    },
                    {
                      path: '/courses/:id/course-dates',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseDatesPage'),
                    },
                    {
                      path: '/courses/:id/course-fee',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseFeePage'),
                    },
                    {
                      path: '/courses/:id/course-type',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseDegreePage'),
                    },
                    {
                      path: '/courses/:id/course-duration-type',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseDurationTypePage'),
                    },
                    {
                      path: '/courses/:id/notes',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseNotesPage'),
                    },
                    {
                      path: '/courses/:id/require-review',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseRequireReviewPage'),
                    },
                    {
                      path: '/courses/:id/uk-institution',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/Edit/CourseUKInstitutionPage'),
                    },
                    {
                      path: '/courses/:id/view',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/Course/CourseViewPage'),
                    },
                    {
                      path: '/degrees',
                      handle: { permissions: ['P74'] },
                      lazy: () => import('./pages/Degree/DegreeListPage'),
                    },
                    {
                      path: '/degrees/add',
                      handle: { permissions: ['P74'] },
                      lazy: () => import('./pages/Degree/DegreeAddPage'),
                    },
                    {
                      path: '/degrees/:id/edit',
                      handle: { permissions: ['P74'] },
                      lazy: () => import('./pages/Degree/DegreeEditPage'),
                    },
                    {
                      path: '/tags',
                      handle: { permissions: ['P78'] },
                      lazy: () => import('./pages/Tag/TagListPage'),
                    },
                    {
                      path: '/tags/add',
                      handle: { permissions: ['P78'] },
                      lazy: () => import('./pages/Tag/TagAddPage'),
                    },
                    {
                      path: '/tags/:id/edit',
                      handle: { permissions: ['P78'] },
                      lazy: () => import('./pages/Tag/TagEditPage'),
                    },
                    {
                      path: '/eligibility-verification',
                      handle: { permissions: ['P37'] },
                      lazy: () => import('./pages/Beneficiary/Admin/EligibilityVerificationPage'),
                    },
                    {
                      path: '/emails/receipts/list',
                      handle: { requireSuperAdmin: true },
                      lazy: () => import('./pages/Email/EmailReceiptListPage'),
                    },
                    {
                      path: '/endorsing-institutions/add',
                      handle: { permissions: ['P64'] },
                      lazy: () => import('./pages/EndorsingInstitution/EndorsingInstitutionAddPage'),
                    },
                    {
                      path: '/endorsing-institutions/:id/edit',
                      handle: { permissions: ['P64'] },
                      lazy: () => import('./pages/EndorsingInstitution/EndorsingInstitutionEditPage'),
                    },
                    {
                      path: '/endorsing-institutions/list',
                      handle: { permissions: ['P64'] },
                      lazy: () => import('./pages/EndorsingInstitution/EndorsingInstitutionListPage'),
                    },
                    {
                      path: '/events/add',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventSurveyAddPage'),
                    },
                    {
                      path: '/events/:id/beneficiaries/edit',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventBeneficiaryAddPage'),
                    },
                    {
                      path: '/events/:id/beneficiaries/manage',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventBeneficiaryManagePage'),
                    },
                    {
                      path: '/events/:id/edit',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventEditPage'),
                    },
                    {
                      path: '/events/:id/survey',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventSurveyViewPage'),
                    },
                    {
                      path: '/events/:id/view',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventViewPage'),
                    },
                    {
                      path: '/events/list',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Event/EventListPage'),
                    },
                    {
                      path: '/events/:eventID/surveys/:surveyID/questions/:questionID/responses',
                      handle: { permissions: ['P12'] },
                      lazy: () => import('./pages/Survey/SurveyQuestionResponseList'),
                    },
                    {
                      path: '/interview-locations/add',
                      handle: { permissions: ['P42'] },
                      lazy: () => import('./pages/InterviewLocation/InterviewLocationAddPage'),
                    },
                    {
                      path: '/interview-locations/:id/edit',
                      handle: { permissions: ['P42'] },
                      lazy: () => import('./pages/InterviewLocation/InterviewLocationEditPage'),
                    },
                    {
                      path: '/interview-locations/list',
                      handle: { permissions: ['P42'] },
                      lazy: () => import('./pages/InterviewLocation/InterviewLocationListPage'),
                    },
                    {
                      path: '/interview-locations/:id/view',
                      handle: { permissions: ['P42'] },
                      lazy: () => import('./pages/InterviewLocation/InterviewLocationViewPage'),
                    },
                    {
                      path: '/partners/list',
                      handle: { permissions: ['P50'] },
                      lazy: () => import('./pages/Partner/PartnerListPage'),
                    },
                    {
                      path: '/partners/add',
                      handle: { permissions: ['P50'] },
                      lazy: () => import('./pages/Partner/PartnerAddPage'),
                    },
                    {
                      path: '/partners/:id/edit',
                      handle: { permissions: ['P50'] },
                      lazy: () => import('./pages/Partner/PartnerEditPage'),
                    },
                    {
                      path: '/partners/:id/view',
                      handle: { permissions: ['P50'] },
                      lazy: () => import('./pages/Partner/PartnerViewPage'),
                    },
                    {
                      path: '/plagiarism-scans',
                      handle: {
                        permissions: ['P38'],
                      },
                      lazy: () => import('./pages/PlagiarismScan/PlagiarismScanPage'),
                    },
                    {
                      path: '/postgrad-courses/list',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/PostgradCourseListPage'),
                    },
                    {
                      path: '/postgrad-courses/import',
                      handle: { permissions: ['P67'] },
                      lazy: () => import('./pages/PostgradCourse/PostgradCourseImportPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/course-dates',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseDatesPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/course-check-level',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseCheckLevelPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/course-fee',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseFeePage'),
                    },
                    {
                      path: '/postgrad-courses/:id/course-fee-cap',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseFeeCapPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/notes',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseNotesPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/require-review',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseRequireReviewPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/uk-institution',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/Edit/PostgradCourseUKInstitutionPage'),
                    },
                    {
                      path: '/postgrad-courses/:id/view',
                      handle: { permissions: ['P47'] },
                      lazy: () => import('./pages/PostgradCourse/PostgradCourseViewPage'),
                    },
                    {
                      path: '/support-requests/list',
                      handle: { permissions: ['P35'] },
                      lazy: () => import('./pages/SupportRequest/SupportRequestListPage'),
                    },
                    {
                      path: '/support-requests/:id/edit',
                      handle: { permissions: ['P35'] },
                      lazy: () => import('./pages/SupportRequest/SupportRequestEditPage'),
                    },
                    {
                      path: '/support-requests/:id/support-request-owner',
                      handle: { permissions: ['P36'] },
                      lazy: () => import('./pages/SupportRequest/SupportRequestUserPage'),
                    },
                    {
                      path: '/support-requests/:id/support-request-status',
                      handle: { permissions: ['P35'] },
                      lazy: () => import('./pages/SupportRequest/SupportRequestStatusPage'),
                    },
                    {
                      path: '/surveys/list',
                      handle: { permissions: ['P13'] },
                      lazy: () => import('./pages/Survey/SurveyListPage'),
                    },
                    {
                      path: '/surveys/:id/view',
                      handle: { permissions: ['P13'] },
                      lazy: () => import('./pages/Survey/SurveyViewPage'),
                    },
                    {
                      path: '/travel-provider',
                      handle: { permissions: ['P68'] },
                      lazy: () => import('./pages/TravelProvider/TravelProviderPage'),
                    },
                    {
                      path: '/uk-institutions/list',
                      handle: { permissions: ['P46'] },
                      lazy: () => import('./pages/UKInstitution/UKInstitutionListPage'),
                    },
                    {
                      path: '/uk-institutions/add',
                      handle: { permissions: ['P46'] },
                      lazy: () => import('./pages/UKInstitution/UKInstitutionAddPage'),
                    },
                    {
                      path: '/uk-institutions/:id/edit',
                      handle: { permissions: ['P46'] },
                      lazy: () => import('./pages/UKInstitution/UKInstitutionEditPage'),
                    },
                    {
                      path: '/users/list',
                      handle: { permissions: ['P34'] },
                      lazy: () => import('./pages/User/UserListPage'),
                    },
                    {
                      path: '/users/add',
                      handle: { permissions: ['P3'] },
                      lazy: () => import('./pages/User/UserAddPage'),
                    },
                    {
                      path: '/users/:id/view',
                      handle: { permissions: ['P2', 'P4'] },
                      lazy: () => import('./pages/User/UserViewPage'),
                    },
                    {
                      path: '/users/:id/edit',
                      lazy: () => import('./pages/User/UserEditPage'),
                    },
                  ],
                },
              ],
            },
          ],
        },

        //Beneficiary routes
        {
          lazy: () => import('./layouts/BeneficiaryLayout'),
          errorElement: <ErrorPage />,
          children: [
            {
              errorElement: <ErrorPage />,
              children: [
                {
                  path: '/alumni-surveys/:id/validate-token/:token',
                  lazy: () => import('./pages/Survey/AlumniSurveyTokenValidator'),
                },
                {
                  path: '/apply/marshall-information',
                  lazy: () => import('./pages/Application/Marshall/ApplicationMarshallPage'),
                },
                {
                  path: '/apply',
                  lazy: () => import('./pages/Application/StartApplicationPage'),
                },
                {
                  path: '/apply/email',
                  element: <Navigate to="/apply" replace />,
                },
                ...[
                  '/application/:id/:token',
                  '/application/:id/:token/:action',
                  '/application/:id/:token/:action/:documentID?',
                  '/beneficiaries/:id/validate-token/:token',
                ].map((path) => ({
                  path,
                  lazy: () => import('./pages/Beneficiary/Self/BeneficiaryTokenValidator'),
                })),
                {
                  path: '/application/access-code-setup',
                  lazy: () => import('./pages/Application/ApplicationAccessCodeSetupPage'),
                },
                {
                  path: '/application/access-code-support',
                  lazy: () => import('./pages/Application/ApplicationAccessCodeSupportPage'),
                },
                {
                  path: '/application/confirm-institution-choice',
                  lazy: () => import('./pages/Application/ApplicationProposedCourseConfirmationPage'),
                },
                {
                  path: '/application/eligibility',
                  lazy: () => import('./pages/Application/ApplicationEligibilityPage'),
                },
                {
                  path: '/application/ineligible',
                  lazy: () => import('./pages/Application/ApplicationIneligiblePage'),
                },
                {
                  path: '/application/locked',
                  lazy: () => import('./pages/Application/ApplicationLockedPage'),
                },
                {
                  path: '/application/recover-access-code',
                  lazy: () => import('./pages/Application/ApplicationAccessCodeRecoveryPage'),
                },
                {
                  path: '/application/request-link',
                  lazy: () => import('./pages/Application/ApplicationRequestLinkPage'),
                },
                {
                  path: '/application/restart',
                  lazy: () => import('./pages/Application/ApplicationRestartPage'),
                },
                {
                  path: '/application/restarted',
                  lazy: () => import('./pages/Application/ApplicationRestartedPage'),
                },
                {
                  path: '/application/validate-access-code',
                  lazy: () => import('./pages/Application/ApplicationAccessCodeValidationPage'),
                },
                {
                  path: '/beneficiaries/logout/:method?',
                  async lazy() {
                    const { loader, BeneficiaryLogoutPage } =
                      await import('./pages/Beneficiary/Self/BeneficiaryLogoutPage');
                    return {
                      loader: loader,
                      Component: BeneficiaryLogoutPage,
                    };
                  },
                },
                {
                  path: '/event-beneficiaries/:id/validate-token/:token',
                  lazy: () => import('./pages/Event/EventBeneficiaryTokenValidator'),
                },
                {
                  path: '/event-beneficiaries/resend-event-email',
                  lazy: () => import('./pages/Event/ResendEventEmailPage'),
                },
                {
                  path: '/recommendation/:id/:token',
                  lazy: () => import('./pages/Application/RecommenderTokenValidator'),
                },
                {
                  path: '/placement/:id/:token',
                  lazy: () => import('./pages/Placement/BeneficiaryPlacementPage'),
                },
                {
                  path: '/placement/:id/:token/course-details',
                  lazy: () => import('./pages/Placement/BeneficiaryPlacementCourseDetailsPage'),
                },
                {
                  path: '/placement/:id/:token/fees',
                  lazy: () => import('./pages/Placement/BeneficiaryPlacementFeesPage'),
                },
                {
                  path: '/placement/:id/:token/relevant-contacts',
                  lazy: () => import('./pages/Placement/BeneficiaryPlacementRelevantContactsPage'),
                },
                {
                  path: '/support-requests/:id/:token',
                  lazy: () => import('./pages/SupportRequest/SupportRequestTokenValidator'),
                },
                {
                  path: '/survey-responses/:id/validate-token/:token',
                  lazy: () => import('./pages/Survey/SurveyResponseTokenValidator'),
                },
                {
                  path: '/transcript/:id/:token',
                  lazy: () => import('./pages/Application/EducationHistoryTokenValidator'),
                },
                { path: '/update-gender', lazy: () => import('./pages/Application/GenderPage') },
                {
                  path: '/upload-transcript',
                  lazy: () => import('./pages/Application/EducationHistoryTranscript'),
                },
                {
                  async lazy() {
                    const { loader, BeneficiaryProtectedRoute } =
                      await import('./pages/Beneficiary/Self/BeneficiaryProtectedRoute');
                    return {
                      loader: loader(queryClient),
                      element: <BeneficiaryProtectedRoute sessionType="application" />,
                    };
                  },
                  children: [
                    {
                      path: '/application/about-you',
                      lazy: () => import('./pages/Application/ApplicationAboutYouPage'),
                    },
                    {
                      path: '/application/acceptance-confirmation',
                      lazy: () => import('./pages/Application/ApplicationAcceptanceConfirmationPage'),
                    },
                    {
                      path: '/application/additional-information',
                      lazy: () => import('./pages/Application/ApplicationAdditionalInformationPage'),
                    },
                    {
                      path: '/application/application-outcomes/:choice_order?',
                      lazy: () => import('./pages/Application/ApplicationProposedCourseStatusPage'),
                    },
                    {
                      path: '/application/citizenship-document',
                      lazy: () => import('./pages/Application/ApplicationCitizenshipDocumentPage'),
                    },
                    {
                      path: '/application/client-care-letter/:study_year?',
                      lazy: () => import('./pages/Application/ApplicationClientCareLetterPage'),
                    },
                    {
                      path: '/application/code-of-conduct-document',
                      lazy: () => import('./pages/Application/ApplicationCodeOfConductDocumentPage'),
                    },
                    {
                      path: '/application/college-offer-letter',
                      lazy: () => import('./pages/Application/ApplicationCollegeOfferLetterPage'),
                    },
                    {
                      path: '/application/defer-offer',
                      lazy: () => import('./pages/Application/ApplicationDeferOfferPage'),
                    },
                    {
                      path: '/application/degree-completion-document',
                      lazy: () => import('./pages/Application/ApplicationDegreeCompletionDocumentPage'),
                    },
                    {
                      path: '/application/education-history',
                      lazy: () => import('./pages/Application/ApplicationEducationHistoryPage'),
                    },
                    {
                      path: '/application/education-history-document',
                      lazy: () => import('./pages/Application/ApplicationEducationHistoryDocumentPage'),
                    },
                    {
                      path: '/application/employment',
                      lazy: () => import('./pages/Application/ApplicationEmploymentPage'),
                    },
                    {
                      path: '/application/equal-opportunities',
                      lazy: () => import('./pages/Application/ApplicationEqualOpportunityPage'),
                    },
                    {
                      path: '/application/essays',
                      lazy: () => import('./pages/Application/ApplicationEssayPage'),
                    },
                    {
                      path: '/application/final-award-letter',
                      lazy: () => import('./pages/Application/ApplicationFinalAwardLetterPage'),
                    },
                    {
                      path: '/application/health-information',
                      lazy: () => import('./pages/Application/ApplicationHealthInformationPage'),
                    },
                    {
                      path: '/application/health-information-document',
                      lazy: () => import('./pages/Application/ApplicationHealthInformationDocumentPage'),
                    },
                    {
                      path: '/application/identification-document',
                      lazy: () => import('./pages/Application/ApplicationIdentificationDocumentPage'),
                    },
                    {
                      path: '/application/immigration',
                      lazy: () => import('./pages/Application/ApplicationImmigrationPage'),
                    },
                    {
                      path: '/application/interview-schedules/list',
                      lazy: () => import('./pages/Application/ApplicationInterviewScheduleListPage'),
                    },
                    {
                      path: '/application/interview-schedules/:id/view',
                      lazy: () => import('./pages/Application/ApplicationInterviewScheduleViewPage'),
                    },
                    {
                      path: '/application/interview-slots/:id/view',
                      lazy: () => import('./pages/Application/ApplicationInterviewSlotViewPage'),
                    },
                    {
                      path: '/application/new-application',
                      lazy: () => import('./pages/Application/ApplicationNewApplicationPage'),
                    },
                    {
                      path: '/application/passport',
                      lazy: () => import('./pages/Application/ApplicationPassportPage'),
                    },
                    {
                      path: '/application/personal-details',
                      lazy: () => import('./pages/Application/ApplicationPersonalDetailsPage'),
                    },
                    {
                      path: '/application/placement-letter',
                      lazy: () => import('./pages/Application/ApplicationPlacementLetterPage'),
                    },
                    {
                      path: '/application/post-scholarship-plan',
                      lazy: () => import('./pages/Application/ApplicationPostScholarshipPlanPage'),
                    },
                    {
                      path: '/application/proposed-courses',
                      lazy: () => import('./pages/Application/ApplicationProposedCoursesPage'),
                    },
                    {
                      path: '/application/publicity-form',
                      lazy: () => import('./pages/Application/ApplicationPublicityFormPage'),
                    },
                    ...['/application/recommenders', '/application/referees'].map((path) => ({
                      path,
                      lazy: () => import('./pages/Application/ApplicationRecommendersPage'),
                    })),
                    {
                      path: '/application/reference-documents',
                      lazy: () => import('./pages/Application/ApplicationReferenceDocumentPage'),
                    },
                    {
                      path: '/application/share-with-advisor',
                      lazy: () => import('./pages/Application/ApplicationShareWithAdvisorPage'),
                    },
                    {
                      path: '/application/signed-application-document',
                      lazy: () => import('./pages/Application/ApplicationSignedApplicationDocumentPage'),
                    },
                    {
                      path: '/application/submit',
                      lazy: () => import('./pages/Application/ApplicationSubmitPage'),
                    },
                    {
                      path: '/application/switch-application',
                      lazy: () => import('./pages/Application/ApplicationSwitchApplicationPage'),
                    },
                    {
                      path: '/application/travel-booking-confirmation',
                      lazy: () => import('./pages/Application/ApplicationTravelBookingConfirmationPage'),
                    },
                    {
                      path: '/application/withdraw',
                      lazy: () => import('./pages/Application/ApplicationWithdrawPage'),
                    },
                    {
                      path: '/application/withdraw-offer',
                      lazy: () => import('./pages/Application/ApplicationWithdrawOfferPage'),
                    },
                    {
                      path: '/application/tasks',
                      lazy: () => import('./pages/Application/ApplicationTaskListPage'),
                    },
                  ],
                },
                {
                  async lazy() {
                    const { loader, BeneficiaryProtectedRoute } =
                      await import('./pages/Beneficiary/Self/BeneficiaryProtectedRoute');
                    return {
                      loader: loader(queryClient),
                      element: <BeneficiaryProtectedRoute sessionType="task" />,
                    };
                  },
                  children: [
                    {
                      path: '/beneficiaries/tasks',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryTaskListPage'),
                    },
                    {
                      path: '/beneficiaries/acceptance-confirmation/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryAcceptanceConfirmationPage'),
                    },
                    {
                      path: '/beneficiaries/application-outcomes/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryProposedCourseStatusPage'),
                    },
                    {
                      path: '/beneficiaries/arrival-date',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryArrivalDatePage'),
                    },
                    {
                      path: '/beneficiaries/cash-card',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryCashCardPage'),
                    },
                    {
                      path: '/beneficiaries/client-care-letter/:study_year?',
                      lazy: () => import('./pages/Application/ApplicationClientCareLetterPage'),
                    },
                    {
                      path: '/beneficiaries/consent-to-contact/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryConsentToContactPage'),
                    },
                    {
                      path: '/beneficiaries/degree-outcome',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryDegreeOutcomePage'),
                    },
                    {
                      path: '/beneficiaries/degree-outcome-marshall/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryDegreeOutcomeMarshallPage'),
                    },
                    {
                      path: '/beneficiaries/departure-date/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryDepartureDatePage'),
                    },
                    {
                      path: '/beneficiaries/dependants',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryDependantsPage'),
                    },
                    {
                      path: '/beneficiaries/final-award-letter/:study_year?',
                      lazy: () => import('./pages/Application/ApplicationFinalAwardLetterPage'),
                    },
                    {
                      path: '/beneficiaries/flights/:flightType',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryFlightsPage'),
                    },
                    {
                      path: '/beneficiaries/future-plans/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryFuturePlansPage'),
                    },
                    {
                      path: '/beneficiaries/next-of-kin/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryNextOfKinPage'),
                    },
                    {
                      path: '/beneficiaries/next-steps/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryNextStepsPage'),
                    },
                    {
                      path: '/beneficiaries/orientation-attendance',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryOrientationPage'),
                    },
                    {
                      path: '/beneficiaries/passport',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryPassportPage'),
                    },
                    {
                      path: '/beneficiaries/passport-status',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryPassportStatusPage'),
                    },
                    {
                      path: '/beneficiaries/pre-departure-event',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryPreDepartureEventPage'),
                    },
                    {
                      path: '/beneficiaries/residence-permit',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryResidencePermitPage'),
                    },
                    {
                      path: '/beneficiaries/scholar-experience-survey/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryScholarExperienceSurveyPage'),
                    },
                    {
                      path: '/beneficiaries/supervisor/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiarySupervisorPage'),
                    },
                    {
                      path: '/beneficiaries/tutor/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryTutorPage'),
                    },
                    {
                      path: '/beneficiaries/uk-address/:study_year?',
                      handle: {
                        contactType: 'uk',
                      },
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryContactPage'),
                    },
                    {
                      path: '/beneficiaries/uk-bank-account',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryUKBankAccountPage'),
                    },
                    {
                      path: '/beneficiaries/uk-phone/:study_year?',
                      handle: {
                        contactType: 'uk',
                      },
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryPhonePage'),
                    },
                    {
                      path: '/beneficiaries/visa/:study_year?',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryVisaPage'),
                    },
                    {
                      path: '/beneficiaries/visa-status',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiaryVisaStatusPage'),
                    },
                  ],
                },
                {
                  async lazy() {
                    const { loader, BeneficiaryProtectedRoute } =
                      await import('./pages/Beneficiary/Self/BeneficiaryProtectedRoute');
                    return {
                      loader: loader(queryClient),
                      element: <BeneficiaryProtectedRoute sessionType="alumni-survey" />,
                    };
                  },
                  children: [
                    {
                      path: '/alumni-surveys/:id/response/add',
                      lazy: () => import('./pages/Survey/AlumniSurveyResponseAddPage'),
                    },
                  ],
                },
                {
                  async lazy() {
                    const { loader, BeneficiaryProtectedRoute } =
                      await import('./pages/Beneficiary/Self/BeneficiaryProtectedRoute');
                    return {
                      loader: loader(queryClient),
                      element: <BeneficiaryProtectedRoute sessionType="event" />,
                    };
                  },
                  children: [
                    {
                      path: '/event-beneficiaries/:id/response/add',
                      lazy: () => import('./pages/Event/EventBeneficiaryResponseAddPage'),
                    },
                  ],
                },
                {
                  async lazy() {
                    const { loader, BeneficiaryProtectedRoute } =
                      await import('./pages/Beneficiary/Self/BeneficiaryProtectedRoute');
                    return {
                      loader: loader(queryClient),
                      element: <BeneficiaryProtectedRoute sessionType="survey-response" />,
                    };
                  },
                  children: [
                    {
                      path: '/survey-responses/:id/add',
                      lazy: () => import('./pages/Survey/SurveyResponseAddPage'),
                    },
                  ],
                },
                {
                  async lazy() {
                    const { loader, BeneficiaryProtectedRoute } =
                      await import('./pages/Beneficiary/Self/BeneficiaryProtectedRoute');
                    return {
                      loader: loader(queryClient),
                      element: <BeneficiaryProtectedRoute sessionType="any" />,
                    };
                  },
                  children: [
                    {
                      path: '/beneficiaries/support',
                      lazy: () => import('./pages/Beneficiary/Self/BeneficiarySupportPage'),
                    },
                  ],
                },
              ],
            },
          ],
        },

        //Public routes
        {
          element: <PublicLayout />,
          errorElement: <ErrorPage />,
          children: [
            {
              path: '/about',
              lazy: () => import('./pages/Public/AboutPage'),
            },
            {
              path: '/cookies',
              lazy: () => import('./pages/Public/CookiesPage'),
            },
            {
              path: '/privacy',
              lazy: () => import('./pages/Public/PrivacyPage'),
            },
            {
              path: '/terms',
              lazy: () => import('./pages/Public/TermsPage'),
            },
          ],
        },
        {
          path: '*',
          lazy: () => import('./pages/NotFoundPage'),
        },
      ],
    },
  ]);

export default function App() {
  const queryClient = useQueryClient();
  const router = getRouter(queryClient);
  return <RouterProvider router={router} />;
}
